<template>
  <div class="region-tree" :class="{ 'region-tree--root': is_root, 'region-tree--nested': !is_root }">
    <v-sheet v-if="is_root" class="region-tree-root-sheet rounded-lg overflow-hidden" elevation="3">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        item-key="id"
        class="region-tree-table region-tree-table--root"
        show-expand
        :expanded.sync="expanded"
        mobile-breakpoint="1"
        dense
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
          <v-btn
            v-if="item.has_children"
            icon
            small
            class="region-tree-expand-btn"
            aria-label="Expandir ou recolher filhos"
            @click.stop="toggleRootExpand(item, isExpanded, expand)"
          >
            <v-progress-circular
              v-if="loading_children_by_id[item.id]"
              indeterminate
              size="14"
              width="2"
              color="primary"
            />
            <v-icon v-else small color="primary">{{ isExpanded ? 'mdi-menu-down' : 'mdi-menu-right' }}</v-icon>
          </v-btn>
          <span v-else class="region-tree-expand-placeholder" />
        </template>

        <template v-slot:item.name="{ item }">
          <span class="region-tree-name">{{ item.name }}</span>
        </template>

        <template v-slot:item.kind="{ item }">
          <v-chip small label outlined :color="kindChipColor(item.kind)" class="region-tree-kind-chip">
            {{ kindLabel(item.kind) }}
          </v-chip>
        </template>

        <template v-slot:item.code="{ item }">
          <span class="text-caption text--secondary">{{ item.code || '—' }}</span>
        </template>

        <template v-slot:item.kilometers="{ item }">
          <div v-if="isEditableKind(item.kind)">
            <v-text-field
              :value="getDisplayValue(item.id, 'kilometers')"
              type="number"
              min="0"
              step="0.01"
              dense
              hide-details
              placeholder="0,00"
              @input="updateDraft(item.id, 'kilometers', $event)"
            />
          </div>
          <span v-else class="text--secondary">{{ getReadOnlyFieldDisplay(item.id, "kilometers") }}</span>
        </template>

        <template v-slot:item.unit_price="{ item }">
          <div v-if="isEditableKind(item.kind)">
            <v-text-field
              :value="getDisplayValue(item.id, 'unit_price')"
              type="number"
              min="0"
              step="0.01"
              dense
              hide-details
              prefix="R$"
              placeholder="0,00"
              @input="updateDraft(item.id, 'unit_price', $event)"
            />
          </div>
          <span v-else class="text--secondary">{{ getReadOnlyFieldDisplay(item.id, "unit_price") }}</span>
        </template>

        <template v-slot:item.discharge_price="{ item }">
          <div v-if="isEditableKind(item.kind)">
            <v-text-field
              :value="getDisplayValue(item.id, 'discharge_price')"
              type="number"
              min="0"
              step="0.01"
              dense
              hide-details
              prefix="R$"
              placeholder="0,00"
              @input="updateDraft(item.id, 'discharge_price', $event)"
            />
          </div>
          <span v-else class="text--secondary">{{ getReadOnlyFieldDisplay(item.id, "discharge_price") }}</span>
        </template>

        <template v-slot:item.total_unit_price="{ item }">
          <span class="font-weight-medium">{{ getTotalUnitPriceDisplay(item.id) }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center">
            <v-btn v-if="canShowSaveAction(item)" x-small color="primary" dark @click.stop="saveRow(item)">
              Salvar
            </v-btn>
            <span v-else class="text-caption text--secondary">—</span>
          </div>
        </template>

        <template v-slot:expanded-item="{ headers: hdr, item }">
          <td :colspan="hdr.length" class="pa-0 region-tree-expanded-td">
            <div class="region-tree-nested-panel">
              <shipping-rate-region-table
                :parent-id="item.id"
                :depth="depth + 1"
                :search="search"
                :global-search="globalSearch"
                :edit-mode="editMode"
                :search-expand-plan="search_expand_plan_for_children"
                :visibility-plan="visibility_plan_for_children"
                :refresh-counter="refreshCounter"
                :drafts-by-region-id="draftsByRegionId"
                :existing-by-region-id="existingByRegionId"
                @update-draft="$emit('update-draft', $event)"
                @save-row="$emit('save-row', $event)"
                @loading-change="setChildLoading"
              />
            </div>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="py-6 text-center text--secondary text-body-2">Nenhuma região neste nível.</div>
        </template>
      </v-data-table>
    </v-sheet>

    <div v-else class="region-branch">
      <div class="region-branch__rail" aria-hidden="true" />
      <div class="region-branch__inner">
        <v-overlay :value="loading" absolute opacity="0.06" color="primary" />
        <v-simple-table dense class="region-branch-table transparent elevation-0">
          <thead>
            <tr>
              <th class="region-branch-th region-branch-th--narrow" />
              <th class="region-branch-th text-left">Nome</th>
              <th class="region-branch-th text-center">Tipo</th>
              <th class="region-branch-th text-center">Código</th>
              <th class="region-branch-th text-center">KM</th>
              <th class="region-branch-th text-center">Preço Frete</th>
              <th class="region-branch-th text-center">Preço Descarga</th>
              <th class="region-branch-th text-center">Preço Final</th>
              <th class="region-branch-th text-center region-branch-th--actions">Ações</th>
            </tr>
          </thead>
          <tbody v-for="item in items" :key="'tbody-' + item.id">
              <tr class="region-branch-row">
                <td class="region-branch-td region-branch-td--narrow">
                  <v-btn
                    v-if="item.has_children"
                    icon
                    x-small
                    class="region-tree-expand-btn"
                    :aria-expanded="expanded_nested_ids.includes(item.id)"
                    @click.stop="toggleNestedExpand(item)"
                  >
                    <v-progress-circular
                      v-if="loading_children_by_id[item.id]"
                      indeterminate
                      size="12"
                      width="2"
                      color="primary"
                    />
                    <v-icon v-else x-small color="primary">
                      {{ expanded_nested_ids.includes(item.id) ? 'mdi-menu-down' : 'mdi-menu-right' }}
                    </v-icon>
                  </v-btn>
                  <span v-else class="region-tree-expand-placeholder region-tree-expand-placeholder--sm" />
                </td>
                <td class="region-branch-td">
                  <span class="region-tree-name text-body-2">{{ item.name }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <v-chip small label outlined :color="kindChipColor(item.kind)" class="region-tree-kind-chip">
                    {{ kindLabel(item.kind) }}
                  </v-chip>
                </td>
                <td class="region-branch-td text-center">
                  <span class="text-caption text--secondary">{{ item.code || '—' }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <div v-if="isEditableKind(item.kind)">
                    <v-text-field
                      :value="getDisplayValue(item.id, 'kilometers')"
                      type="number"
                      min="0"
                      step="0.01"
                      dense
                      hide-details
                      placeholder="0,00"
                      @input="updateDraft(item.id, 'kilometers', $event)"
                    />
                  </div>
                  <span v-else class="text--secondary">{{ getReadOnlyFieldDisplay(item.id, "kilometers") }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <div v-if="isEditableKind(item.kind)">
                    <v-text-field
                      :value="getDisplayValue(item.id, 'unit_price')"
                      type="number"
                      min="0"
                      step="0.01"
                      dense
                      hide-details
                      prefix="R$"
                      placeholder="0,00"
                      @input="updateDraft(item.id, 'unit_price', $event)"
                    />
                  </div>
                  <span v-else class="text--secondary">{{ getReadOnlyFieldDisplay(item.id, "unit_price") }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <div v-if="isEditableKind(item.kind)">
                    <v-text-field
                      :value="getDisplayValue(item.id, 'discharge_price')"
                      type="number"
                      min="0"
                      step="0.01"
                      dense
                      hide-details
                      prefix="R$"
                      placeholder="0,00"
                      @input="updateDraft(item.id, 'discharge_price', $event)"
                    />
                  </div>
                  <span v-else class="text--secondary">{{ getReadOnlyFieldDisplay(item.id, "discharge_price") }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <span class="font-weight-medium">{{ getTotalUnitPriceDisplay(item.id) }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <v-btn v-if="canShowSaveAction(item)" x-small color="primary" dark @click.stop="saveRow(item)">
                    Salvar
                  </v-btn>
                  <span v-else class="text-caption text--secondary">—</span>
                </td>
              </tr>
              <tr v-if="expanded_nested_ids.includes(item.id) && item.has_children" class="region-branch-subrow">
                <td colspan="9" class="pa-0 region-tree-expanded-td region-tree-expanded-td--nested">
                  <div class="region-tree-nested-panel region-tree-nested-panel--deep">
                    <shipping-rate-region-table
                      :parent-id="item.id"
                      :depth="depth + 1"
                      :search="search"
                      :global-search="globalSearch"
                      :edit-mode="editMode"
                      :search-expand-plan="search_expand_plan_for_children"
                      :visibility-plan="visibility_plan_for_children"
                      :refresh-counter="refreshCounter"
                      :drafts-by-region-id="draftsByRegionId"
                      :existing-by-region-id="existingByRegionId"
                      @update-draft="$emit('update-draft', $event)"
                      @save-row="$emit('save-row', $event)"
                      @loading-change="setChildLoading"
                    />
                  </div>
                </td>
              </tr>
          </tbody>
        </v-simple-table>

        <div v-if="!loading && items.length === 0" class="py-5 text-center text--secondary text-body-2">
          Nenhuma região neste nível.
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Api from '@/api/index'
import {
  flattenRegionsTree,
  buildAncestorExpandPlan,
  unwrapRegionsIndexPayload,
  dedupeRegionRowsPreferDeepestPath,
} from '@/_backoffice/_registrations/components/region/flattenRegionsTree.js'

let KIND_LABELS = {
  country: 'País',
  state: 'Estado',
  rgint: 'Região Geográfica Intermediária',
  rgi: 'Região Geográfica Imediata',
  city: 'Município',
  regional: 'Zona',
  neighborhood: 'Bairro',
}

let DEFAULT_EXPAND_KINDS = {
  0: ['country'],
  1: ['state'],
  2: ['rgint'],
}

export default {
  name: 'ShippingRateRegionTable',
  components: {
    ShippingRateRegionTable: () => import('./ShippingRateRegionTable.vue'),
  },
  props: {
    parentId: {
      type: Number,
      default: null,
    },
    depth: {
      type: Number,
      default: 0,
    },
    search: {
      type: String,
      default: '',
    },
    globalSearch: {
      type: Boolean,
      default: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    refreshCounter: {
      type: Number,
      default: 0,
    },
    searchExpandPlan: {
      type: Object,
      default: null,
    },
    visibilityPlan: {
      type: Object,
      default: null,
    },
    draftsByRegionId: {
      type: Object,
      default: () => ({}),
    },
    existingByRegionId: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      expanded: [],
      expanded_nested_ids: [],
      loading_children_by_id: {},
      active_search_expand_plan: null,
      active_visibility_plan: null,
      headers: [
        { text: 'Nome', value: 'name', sortable: false },
        { text: 'Tipo', value: 'kind', sortable: false, align: 'center', width: '120px' },
        { text: 'Código', value: 'code', sortable: false, align: 'center', width: '90px' },
        { text: 'KM', value: 'kilometers', sortable: false, align: 'center', width: '110px' },
        { text: 'Preço Frete', value: 'unit_price', sortable: false, align: 'center', width: '130px' },
        { text: 'Preço Descarga', value: 'discharge_price', sortable: false, align: 'center', width: '140px' },
        { text: 'Preço Final', value: 'total_unit_price', sortable: false, align: 'center', width: '130px' },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center', width: '110px' },
      ],
    }
  },
  computed: {
    is_root() {
      return this.depth === 0
    },
    kinds_to_expand_by_default() {
      return DEFAULT_EXPAND_KINDS[this.depth] || []
    },
    search_expand_plan_for_children() {
      return this.is_root ? this.active_search_expand_plan : this.searchExpandPlan
    },
    visibility_plan_for_children() {
      return this.is_root ? this.active_visibility_plan : this.visibilityPlan
    },
    trimmed_search_text() {
      return (this.search || '').trim()
    },
    search_text_lowercase() {
      return this.trimmed_search_text.toLowerCase()
    },
    has_global_search_term() {
      return this.globalSearch && this.trimmed_search_text.length > 0
    },
  },
  methods: {
    kindLabel(kind) {
      return KIND_LABELS[kind] || kind
    },
    kindChipColor(kind) {
      let color_by_kind = {
        country: 'deep-purple',
        state: 'indigo',
        rgint: 'cyan darken-2',
        rgi: 'blue darken-2',
        city: 'teal',
        regional: 'amber darken-2',
        neighborhood: 'brown',
      }
      return color_by_kind[kind] || 'grey'
    },
    isEditableKind(kind) {
      return this.editMode && ['city', 'regional'].includes(kind)
    },
    getDraftValue(regionId, field) {
      return this.draftsByRegionId?.[String(regionId)]?.[field] ?? ''
    },
    getDisplayValue(regionId, field) {
      let draftValue = this.getDraftValue(regionId, field)
      if (draftValue !== '' && draftValue != null) return draftValue
      let existing = this.existingRate(regionId)
      if (!existing) return ''
      if (field === 'kilometers') return existing?.attributes?.kilometers ?? ''
      if (field === 'unit_price') return existing?.attributes?.unit_price ?? ''
      if (field === 'discharge_price') return existing?.attributes?.discharge_price ?? ''
      return ''
    },
    hasDraft(regionId) {
      return Object.prototype.hasOwnProperty.call(this.draftsByRegionId || {}, String(regionId))
    },
    updateDraft(regionId, field, value) {
      this.$emit('update-draft', { regionId: Number(regionId), field, value })
    },
    existingRate(regionId) {
      return this.existingByRegionId?.[String(regionId)] || null
    },
    hasExistingRate(regionId) {
      return Boolean(this.existingRate(regionId))
    },
    canShowSaveAction(item) {
      if (!this.editMode) return false
      if (!this.isEditableKind(item?.kind)) return false
      let regionId = Number(item?.id)
      if (!this.hasDraft(regionId)) return false
      let draft = this.draftsByRegionId?.[String(item.id)] || {}
      let hasKm = draft.kilometers !== '' && draft.kilometers != null
      let hasPrice = draft.unit_price !== '' && draft.unit_price != null
      let hasDischarge = draft.discharge_price !== '' && draft.discharge_price != null
      if (!hasKm && !hasPrice && !hasDischarge) return false
      if (!this.hasExistingRate(regionId)) return true
      let existing = this.existingRate(regionId)
      let currentKm = Number(existing?.attributes?.kilometers || 0)
      let currentPrice = Number(existing?.attributes?.unit_price || 0)
      let currentDischarge = Number(existing?.attributes?.discharge_price || 0)
      let nextKm = Number(hasKm ? draft.kilometers : currentKm)
      let nextPrice = Number(hasPrice ? draft.unit_price : currentPrice)
      let nextDischarge = Number(hasDischarge ? draft.discharge_price : currentDischarge)
      return nextKm !== currentKm || nextPrice !== currentPrice || nextDischarge !== currentDischarge
    },
    saveRow(item) {
      this.$emit('save-row', { regionId: Number(item.id) })
    },
    getReadOnlyFieldDisplay(regionId, field) {
      let existing = this.existingRate(regionId)
      if (!existing) return "—"
      if (field === "kilometers") return existing?.attributes?.kilometers ?? "—"
      if (field === "unit_price") return existing?.attributes?.unit_price ?? "—"
      if (field === "discharge_price") return existing?.attributes?.discharge_price ?? "—"
      return "—"
    },
    getTotalUnitPriceDisplay(regionId) {
      const freight = Number(this.getDisplayValue(regionId, 'unit_price') || 0)
      const discharge = Number(this.getDisplayValue(regionId, 'discharge_price') || 0)
      if (!this.hasExistingRate(regionId) && !this.hasDraft(regionId)) return "—"
      const total = (Number.isFinite(freight) ? freight : 0) + (Number.isFinite(discharge) ? discharge : 0)
      return Number(total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    buildVisibilityPlanFromExistingRates(flatRows) {
      let existing_region_ids = new Set(Object.keys(this.existingByRegionId || {}).map((id) => Number(id)))
      let visible_ids = new Set()
      let root_ids = new Set()
      let children_by_parent_id = {}
      let expandable_parent_ids = new Set()

      for (let row of flatRows || []) {
        let row_id = Number(row.id)
        if (!existing_region_ids.has(row_id)) continue
        let chain_ids = [...(row.ancestor_ids || []).map((id) => Number(id)), row_id]
        for (let id of chain_ids) visible_ids.add(Number(id))
        if (chain_ids.length) root_ids.add(chain_ids[0])
        for (let i = 0; i < chain_ids.length - 1; i++) {
          let parent_id = chain_ids[i]
          let child_id = chain_ids[i + 1]
          let parent_key = String(parent_id)
          if (!children_by_parent_id[parent_key]) children_by_parent_id[parent_key] = new Set()
          children_by_parent_id[parent_key].add(child_id)
          expandable_parent_ids.add(parent_id)
        }
      }

      let visible_children_under_parent = {}
      Object.keys(children_by_parent_id).forEach((parent_key) => {
        visible_children_under_parent[parent_key] = [...children_by_parent_id[parent_key]]
      })

      return {
        visible_ids: [...visible_ids],
        visible_root_ids: [...root_ids],
        visible_children_under_parent,
        expand_root_ids: [...root_ids].filter((id) => expandable_parent_ids.has(id)),
        expand_children_under_parent: visible_children_under_parent,
      }
    },
    applyVisibilityPlanAtLevel() {
      if (this.editMode) return
      let plan = this.visibility_plan_for_children
      if (!plan) return

      if (this.is_root) {
        let root_set = new Set((plan.visible_root_ids || []).map((id) => Number(id)))
        this.items = this.items.filter((row) => root_set.has(Number(row.id)))
      } else {
        let parent_key = String(Number(this.parentId))
        let child_set = new Set((plan.visible_children_under_parent?.[parent_key] || []).map((id) => Number(id)))
        this.items = this.items.filter((row) => child_set.has(Number(row.id)))
      }
      this.total = this.items.length
    },
    applyVisibilityExpansionAtLevel() {
      if (this.editMode) return
      let plan = this.visibility_plan_for_children
      if (!plan) return

      if (this.is_root) {
        let expand_root_set = new Set((plan.expand_root_ids || []).map((id) => Number(id)))
        this.expanded = this.items.filter((row) => expand_root_set.has(Number(row.id)))
        return
      }
      let parent_key = String(Number(this.parentId))
      let nested_to_expand = (plan.expand_children_under_parent?.[parent_key] || [])
        .map((id) => Number(id))
        .filter((id) => this.items.some((row) => Number(row.id) === id && row.has_children))
      this.expanded_nested_ids = [...new Set([...this.expanded_nested_ids.map(Number), ...nested_to_expand])]
    },
    toggleNestedExpand(item) {
      let region_id = Number(item.id)
      let is_opening_branch = !this.expanded_nested_ids.includes(region_id)
      if (is_opening_branch) {
        this.loading_children_by_id = { ...this.loading_children_by_id, [region_id]: true }
      } else {
        this.loading_children_by_id = { ...this.loading_children_by_id, [region_id]: false }
      }
      let existing_index = this.expanded_nested_ids.indexOf(region_id)
      let next_expanded_nested_ids = [...this.expanded_nested_ids]
      if (existing_index === -1) next_expanded_nested_ids.push(region_id)
      else next_expanded_nested_ids.splice(existing_index, 1)
      this.expanded_nested_ids = next_expanded_nested_ids
    },
    toggleRootExpand(item, isExpanded, expand) {
      if (!isExpanded) {
        this.loading_children_by_id = { ...this.loading_children_by_id, [item.id]: true }
      } else {
        this.loading_children_by_id = { ...this.loading_children_by_id, [item.id]: false }
      }
      expand(!isExpanded)
    },
    setChildLoading(payload = {}) {
      let parent_region_id = payload.parentId
      let is_loading_children = payload.loading
      if (!parent_region_id) return
      this.loading_children_by_id = {
        ...this.loading_children_by_id,
        [parent_region_id]: Boolean(is_loading_children),
      }
    },
    applySearchExpandPlanAtLevel() {
      let search_expand_plan = this.search_expand_plan_for_children
      if (!this.has_global_search_term || !search_expand_plan) return

      if (this.is_root) {
        let expanded_root_id_set = new Set((search_expand_plan.expand_root_ids || []).map((id) => Number(id)))
        this.expanded = this.items.filter((row) => expanded_root_id_set.has(Number(row.id)))
        return
      }

      let parent_region_id = Number(this.parentId)
      let parent_region_key = String(parent_region_id)
      let child_ids_to_expand_from_plan = search_expand_plan.expand_children_under_parent[parent_region_key] || []
      let child_ids_to_expand_set = new Set(child_ids_to_expand_from_plan.map((id) => Number(id)))
      let nested_row_ids_to_expand = this.items
        .filter((row) => child_ids_to_expand_set.has(Number(row.id)))
        .map((row) => Number(row.id))
      this.expanded_nested_ids = [...new Set([...this.expanded_nested_ids.map(Number), ...nested_row_ids_to_expand])]
    },
    applyDefaultExpansion() {
      if (this.has_global_search_term) return

      let kinds = this.kinds_to_expand_by_default
      if (!kinds.length || !this.items.length) return
      let rows_matching_default_kinds = this.items.filter((row) => row.has_children && kinds.includes(row.kind))
      if (!rows_matching_default_kinds.length) return

      this.$nextTick(() => {
        if (this.is_root) {
          this.expanded = [...rows_matching_default_kinds]
        } else {
          let default_expanded_nested_ids = rows_matching_default_kinds.map((row) => Number(row.id))
          this.expanded_nested_ids = [...new Set([...this.expanded_nested_ids, ...default_expanded_nested_ids])]
        }
      })
    },
    fetchNodes() {
      this.loading = true
      this.$emit('loading-change', { parentId: this.parentId, loading: true })
      let search_term_lowercase = this.search_text_lowercase
      if (!this.editMode && this.is_root) {
        this.active_search_expand_plan = null
        Api.Region.index()
          .then((res) => res.data)
          .then((raw_tree) => {
            let tree = unwrapRegionsIndexPayload(raw_tree)
            let flat = dedupeRegionRowsPreferDeepestPath(flattenRegionsTree(tree))
            this.active_visibility_plan = this.buildVisibilityPlanFromExistingRates(flat)
            if (!this.active_visibility_plan.visible_ids.length) {
              this.items = []
              this.total = 0
              this.expanded = []
              return Promise.resolve(null)
            }
            return Api.Region.treeChildren({ all: true })
          })
          .then((maybe_res) => {
            if (!maybe_res) return
            let body = maybe_res.data
            this.items = Array.isArray(body.data) ? body.data : []
            this.applyVisibilityPlanAtLevel()
          })
          .catch(() => {
            this.items = []
            this.total = 0
            this.expanded = []
            this.active_visibility_plan = null
          })
          .finally(() => {
            this.loading = false
            this.$nextTick(() => {
              this.applyVisibilityExpansionAtLevel()
            })
            this.$emit('loading-change', { parentId: this.parentId, loading: false })
          })
        return
      }
      if (this.globalSearch && this.is_root) {
        this.active_search_expand_plan = null
        Api.Region.index()
          .then((res) => res.data)
          .then((raw_tree) => {
            let tree = unwrapRegionsIndexPayload(raw_tree)
            let flat = dedupeRegionRowsPreferDeepestPath(flattenRegionsTree(tree))
            let matches = flat.filter((region_row) => {
              if (!search_term_lowercase) return true
              return String(region_row.name || '').toLowerCase().includes(search_term_lowercase)
            })
            if (!matches.length) {
              this.items = []
              this.total = 0
              this.expanded = []
              return Promise.resolve(null)
            }
            this.active_search_expand_plan = buildAncestorExpandPlan(matches)
            return Api.Region.treeChildren({ all: true })
          })
          .then((maybe_res) => {
            if (!maybe_res) return
            let body = maybe_res.data
            this.items = Array.isArray(body.data) ? body.data : []
            this.total = body.meta && typeof body.meta.total === 'number' ? body.meta.total : 0
          })
          .catch(() => {
            this.items = []
            this.total = 0
            this.expanded = []
            this.active_search_expand_plan = null
          })
          .finally(() => {
            this.loading = false
            this.$nextTick(() => {
              this.applySearchExpandPlanAtLevel()
            })
            this.$emit('loading-change', { parentId: this.parentId, loading: false })
          })
      } else {
        if (this.is_root) this.active_search_expand_plan = null
        let params = { all: true }
        if (this.parentId != null) params.parent_id = this.parentId
        if (search_term_lowercase && !this.globalSearch) params.q = search_term_lowercase

        Api.Region.treeChildren(params)
          .then((res) => res.data)
          .then((body) => {
            this.items = Array.isArray(body.data) ? body.data : []
            this.total = body.meta && typeof body.meta.total === 'number' ? body.meta.total : 0
            this.applyVisibilityPlanAtLevel()
          })
          .catch(() => {
            this.items = []
            this.total = 0
          })
          .finally(() => {
            this.loading = false
            this.$nextTick(() => {
              this.applySearchExpandPlanAtLevel()
              this.applyVisibilityExpansionAtLevel()
              this.applyDefaultExpansion()
            })
            this.$emit('loading-change', { parentId: this.parentId, loading: false })
          })
      }
    },
  },
  mounted() {
    this.fetchNodes()
  },
  watch: {
    search() {
      this.fetchNodes()
    },
    globalSearch() {
      this.fetchNodes()
    },
    refreshCounter() {
      this.expanded = []
      this.expanded_nested_ids = []
      if (this.is_root) this.active_search_expand_plan = null
      if (this.is_root) this.active_visibility_plan = null
      this.fetchNodes()
    },
    editMode() {
      this.expanded = []
      this.expanded_nested_ids = []
      if (this.is_root) {
        this.active_search_expand_plan = null
        this.active_visibility_plan = null
      }
      this.fetchNodes()
    },
    items() {
      if (!this.has_global_search_term) return
      this.$nextTick(() => {
        this.applySearchExpandPlanAtLevel()
      })
    },
  },
}
</script>

<style scoped>
.region-tree-root-sheet {
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.region-tree-table--root >>> thead tr th {
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.54) !important;
  background: #fafbfc !important;
}

.region-tree-table--root >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}

.region-tree-expand-placeholder {
  display: inline-block;
  width: 36px;
  height: 36px;
  vertical-align: middle;
}

.region-tree-expand-placeholder--sm {
  width: 28px;
  height: 28px;
}

.region-tree-name {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.82);
}

.region-tree-kind-chip {
  font-size: 11px !important;
  height: 22px !important;
}

.region-tree-expanded-td {
  background: linear-gradient(180deg, #f5f7fa 0%, #fafbfc 100%);
  border-top: none !important;
}

.region-tree-expanded-td--nested {
  background: #eef2f7;
}

.region-tree-nested-panel {
  padding: 12px 12px 8px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.region-tree-nested-panel--deep {
  padding-left: 12px;
}

.region-branch {
  display: flex;
  align-items: stretch;
  position: relative;
  min-height: 48px;
}

.region-branch__rail {
  flex: 0 0 4px;
  margin-right: 6px;
  border-radius: 4px 0 0 4px;
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.35), rgba(25, 118, 210, 0.08));
}

.region-branch__inner {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  border-radius: 0 10px 10px 0;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background: #fff;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.region-branch-th {
  font-size: 0.65rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.48) !important;
  height: 36px !important;
  background: #f5f7fa !important;
}

.region-branch-th--narrow {
  width: 44px;
}

.region-branch-th--actions {
  width: 140px;
}

.region-branch-row:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}

.region-branch-td {
  vertical-align: middle !important;
}

.region-branch-td--narrow {
  width: 44px;
}

.region-branch-subrow td {
  border-top: none !important;
}
</style>
